import React, { useState, useEffect } from 'react';
import api from '../../../api.js';
import { AlertCircle, Building, UserPlus, X } from 'lucide-react';

function DriverAssignmentModal({ isOpen, onClose, onSuccess, onMessage, vehicle, drivers, currentAssignment }) {
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [purpose, setPurpose] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedDriverId) {
      onMessage && onMessage({
        title: 'Erreur',
        message: 'Veuillez sélectionner un conducteur',
        type: 'error'
      });
      return;
    }

    try {
      // Create a trip record to represent the assignment
      const driver = drivers.find(d => d.id === parseInt(selectedDriverId));
      const tripData = {
        org_id: vehicle.org_id || driver?.org_id,
        vehicle_id: vehicle.id,
        driver_id: parseInt(selectedDriverId),
        start_date: startDate,
        start_mileage: vehicle.mileage || 0,
        purpose: purpose || 'Assignment',
        status: 'in_progress'
      };

      await api.trip.create(tripData);

      onMessage && onMessage({
        title: 'Succès',
        message: 'Conducteur affecté avec succès',
        type: 'success'
      });
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error assigning driver:', error);
      onMessage && onMessage({
        title: 'Erreur',
        message: `Erreur lors de l'affectation: ${error.message}`,
        type: 'error'
      });
    }
  };

  if (!isOpen) return null;

  // Block assignment if vehicle is in maintenance
  const isInMaintenance = vehicle?.status === 'maintenance';

  // Check if driver meets vehicle qualification requirements
  const checkDriverQualifications = (driver, vehicle) => {
    if (!vehicle) return { isQualified: false, missingQualifications: [] };

    const missing = [];

    // Check required license category
    const requiredLicense = vehicle.required_license_category || 'B';
    const licenseMap = {
      'B': 'license_category_b',
      'C': 'license_category_c',
      'D': 'license_category_d',
      'CE': 'license_category_ce'
    };

    const hasRequiredLicense = driver[licenseMap[requiredLicense]];
    if (!hasRequiredLicense) {
      missing.push(`Permis ${requiredLicense}`);
    }

    // Check certifications
    if (vehicle.requires_cert_adr && !driver.cert_adr) {
      missing.push('Certificat ADR');
    }
    if (vehicle.requires_cert_fimo && !driver.cert_fimo) {
      missing.push('Certificat FIMO');
    }
    if (vehicle.requires_cert_crane && !driver.cert_crane) {
      missing.push('Certificat Grue');
    }
    if (vehicle.requires_cert_forklift && !driver.cert_forklift) {
      missing.push('Certificat Chariot Élévateur');
    }

    return {
      isQualified: missing.length === 0,
      missingQualifications: missing
    };
  };

  // Filter drivers to only show those from the same organization as the vehicle
  const eligibleDrivers = vehicle?.org_id
    ? drivers.filter(d => d.org_id === vehicle.org_id)
    : [];

  // Further filter to only qualified drivers and categorize them
  const qualifiedDrivers = eligibleDrivers.filter(d => {
    const quals = checkDriverQualifications(d, vehicle);
    return quals.isQualified;
  });

  const unqualifiedDrivers = eligibleDrivers.filter(d => {
    const quals = checkDriverQualifications(d, vehicle);
    return !quals.isQualified;
  });

  const hasOrganization = !!vehicle?.org_id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold">Affecter un Conducteur</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {isInMaintenance && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
              <div>
                <div className="text-sm font-medium text-amber-800">Véhicule indisponible</div>
                <div className="text-xs text-amber-600">Ce véhicule est actuellement en maintenance. L'affectation sera possible après retour du garage.</div>
              </div>
            </div>
          )}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-2">Véhicule</h3>
            <p className="text-gray-700">{vehicle?.make} {vehicle?.model} - {vehicle?.license_plate}</p>
            {vehicle?.org_name && (
              <p className="text-sm text-gray-600 mt-1">
                <Building className="w-4 h-4 inline mr-1" />
                Organisation: {vehicle.org_name}
              </p>
            )}

            {/* Show required qualifications */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-700 mb-1">Qualifications requises:</p>
              <div className="flex flex-wrap gap-2">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  Permis {vehicle?.required_license_category || 'B'}
                </span>
                {vehicle?.requires_cert_adr && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">ADR</span>
                )}
                {vehicle?.requires_cert_fimo && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">FIMO</span>
                )}
                {vehicle?.requires_cert_crane && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Grue</span>
                )}
                {vehicle?.requires_cert_forklift && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">Chariot Élévateur</span>
                )}
              </div>
            </div>
          </div>

          {!hasOrganization && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Organisation requise</p>
                <p className="text-sm text-amber-800">
                  Merci d'affecter le véhicule à une organisation avant d'assigner un conducteur.
                </p>
              </div>
            </div>
          )}

          {currentAssignment && (
            <div className="bg-red-50 border border-red-300 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-900">Le véhicule est déjà affecté à {currentAssignment.driver.first_name} {currentAssignment.driver.last_name}</p>
                <p className="text-sm text-red-800 mt-1">
                  Pour affecter un nouveau conducteur, vous devez d'abord désaffecter le conducteur actuel.
                </p>
                <p className="text-sm text-red-800 mt-2">
                  <strong>Comment désaffecter :</strong> Fermez cette fenêtre, puis cliquez sur l'icône de corbeille rouge à côté du conducteur actuel.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Conducteur *</label>

            {/* Show warning if there are unqualified drivers */}
            {hasOrganization && unqualifiedDrivers.length > 0 && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-orange-900 font-medium mb-1">
                  {unqualifiedDrivers.length} conducteur(s) non qualifié(s):
                </p>
                <ul className="text-xs text-orange-800 space-y-1">
                  {unqualifiedDrivers.map(driver => {
                    const quals = checkDriverQualifications(driver, vehicle);
                    return (
                      <li key={driver.id}>
                        <strong>{driver.first_name} {driver.last_name}</strong> - Manque: {quals.missingQualifications.join(', ')}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {hasOrganization && eligibleDrivers.length > 0 && qualifiedDrivers.length === 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-red-800">
                  Aucun conducteur qualifié dans l'organisation <strong>{vehicle.org_name}</strong>.
                  Tous les conducteurs manquent de qualifications requises.
                </p>
              </div>
            )}

            {hasOrganization && eligibleDrivers.length === 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-sm text-blue-800">
                  Aucun conducteur disponible dans l'organisation <strong>{vehicle.org_name}</strong>.
                </p>
              </div>
            )}

            <select
              value={selectedDriverId}
              onChange={(e) => setSelectedDriverId(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg disabled:bg-gray-100 disabled:cursor-not-allowed"
              required
              disabled={!hasOrganization || qualifiedDrivers.length === 0 || currentAssignment}
            >
              <option value="">
                {!hasOrganization
                  ? "Merci d'affecter le véhicule à une organisation"
                  : qualifiedDrivers.length === 0
                  ? "Aucun conducteur qualifié disponible"
                  : "Sélectionner un conducteur..."}
              </option>
              {qualifiedDrivers.map(driver => (
                <option key={driver.id} value={driver.id}>
                  {driver.first_name} {driver.last_name} - {driver.email}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Date de début</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">But du trajet</label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
              placeholder="Ex: Livraison, Service client..."
            />
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={!hasOrganization || qualifiedDrivers.length === 0 || currentAssignment || isInMaintenance}
            >
              <UserPlus className="w-5 h-5" />
              {isInMaintenance ? 'Indisponible' : 'Affecter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// --- LOCATION ASSIGNMENT MODAL (WITH WORKFLOW) ---

export default DriverAssignmentModal;
